import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { crmSettingsApi } from "../../../api/crmSettings";
import styles from "../CRMSettingsPage.module.css";
const SLOT_OPTIONS = [5, 10, 15, 30, 60];
const MIN_AHEAD_OPTIONS = [0, 5, 10, 15, 30, 60];

function SelectField({ label, help, children, ...props }) {
  return (
    <div className={styles.workScheduleField}>
      <label className={styles.workScheduleLabel}>{label}</label>
      <select className={styles.workScheduleControl} {...props}>{children}</select>
      {help ? <div className={styles.workScheduleHelp}>{help}</div> : null}
    </div>
  );
}

function ModeCard({ active, title, badge, description, onClick }) {
  return (
    <button
      type="button"
      className={`${styles.workScheduleModeCard} ${active ? styles.workScheduleModeCardActive : ""}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <div className={styles.workScheduleModeTop}>
        <div className={styles.workScheduleModeName}><span className={styles.workScheduleModeRadio} />{title}</div>
        <span className={styles.workScheduleModeBadge}>{badge}</span>
      </div>
      <div className={styles.workScheduleModeDesc}>{description}</div>
    </button>
  );
}

function Choice({ checked, name, value, title, description, onChange }) {
  return (
    <label className={`${styles.workScheduleChoice} ${checked ? styles.workScheduleChoiceActive : ""}`}>
      <input type="radio" name={name} value={value} checked={checked} onChange={onChange} />
      <span>
        <div className={styles.workScheduleChoiceTitle}>{title}</div>
        <div className={styles.workScheduleChoiceDesc}>{description}</div>
      </span>
    </label>
  );
}

export default function WorkScheduleTab({
  enabled,
  setEnabled,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  slotMinutes,
  setSlotMinutes,
  minAheadMinutes,
  setMinAheadMinutes,
  mode,
  setMode,
  slotGeneration,
  setSlotGeneration,
  appointmentCount,
  setAppointmentCount,
}) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [importMode, setImportMode] = useState("add");
  const [collisionMode, setCollisionMode] = useState("add");
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useState(null);
  const [editing, setEditing] = useState(null);

  const invalidRange = Boolean(startTime && endTime && startTime >= endTime);

  const locale = useMemo(() => {
    const lng = String(i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
    if (lng.startsWith("ru")) return "ru-RU";
    if (lng.startsWith("et") || lng === "ee") return "et-EE";
    if (lng.startsWith("es")) return "es-ES";
    return "en-GB";
  }, [i18n.resolvedLanguage, i18n.language]);

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(date);
  };

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await crmSettingsApi.getWorkScheduleSlots();
      setRows(data.items || []);
      setStatus(data.status || null);
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recurringPreview = useMemo(() => {
    if (invalidRange || !startTime || !endTime) return [];
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    if (end <= start) return [];

    const values = [];
    if (slotGeneration === "appointment_count") {
      const count = Math.max(1, Number(appointmentCount || 1));
      for (let i = 0; i < count; i += 1) {
        const minute = start + Math.floor(((end - start) * i) / count);
        values.push(`${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`);
      }
    } else {
      for (let minute = start; minute < end; minute += Number(slotMinutes || 15)) {
        values.push(`${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`);
      }
    }
    return values;
  }, [invalidRange, startTime, endTime, slotMinutes, slotGeneration, appointmentCount]);

  const groupedRows = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (!map.has(row.date)) map.set(row.date, []);
      map.get(row.date).push(row);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, items]) => ({ date, items: items.sort((a, b) => a.time.localeCompare(b.time)) }));
  }, [rows]);

  const previewCount = (preview?.items || []).reduce((sum, item) => sum + (item.times?.length || 0), 0);

  const doPreview = async () => {
    setFlash(null);
    setBusy("preview");
    try {
      setPreview(await crmSettingsApi.previewWorkSchedule(text));
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.preview_error") });
    } finally {
      setBusy("");
    }
  };

  const doImport = async () => {
    setFlash(null);
    setBusy("import");
    try {
      const data = await crmSettingsApi.importWorkSchedule({
        text,
        import_mode: importMode,
        collision_mode: collisionMode,
      });
      setRows(data.items || []);
      setStatus(data.status || null);
      setPreview(null);
      setText("");
      setFlash({ type: "ok", text: t("work_schedule.import_success") });
      await refresh();
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setBusy("");
    }
  };

  const addOne = async () => {
    if (!newDate || !newTime) return;
    setFlash(null);
    setBusy("add");
    try {
      await crmSettingsApi.addWorkScheduleSlot({ schedule_date: newDate, start_time: newTime });
      setNewTime("");
      setFlash({ type: "ok", text: t("work_schedule.add_success") });
      await refresh();
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setBusy("");
    }
  };

  const saveEdit = async () => {
    if (!editing?.id || !editing.date || !editing.time) return;
    setFlash(null);
    setBusy(`edit-${editing.id}`);
    try {
      await crmSettingsApi.updateWorkScheduleSlot(editing.id, {
        schedule_date: editing.date,
        start_time: editing.time,
      });
      setEditing(null);
      setFlash({ type: "ok", text: t("work_schedule.update_success") });
      await refresh();
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setBusy("");
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm(t("work_schedule.confirm_delete"))) return;
    setFlash(null);
    setBusy(`delete-${id}`);
    try {
      await crmSettingsApi.deleteWorkScheduleSlot(id);
      setFlash({ type: "ok", text: t("work_schedule.delete_success") });
      await refresh();
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className={`${styles.card} ${styles.workScheduleRoot}`}>
      <div className={styles.workScheduleHero}>
        <div className={styles.workScheduleHeroText}>
          <h3 className={styles.workScheduleTitle}>{t("work_schedule.title")}</h3>
          <p className={styles.workScheduleSubtitle}>{t("work_schedule.subtitle")}</p>
        </div>
        <div className={styles.workScheduleSwitchWrap}>
          <div className={styles.workScheduleSwitchCopy}>
            <div className={styles.workScheduleSwitchTitle}>{t("work_schedule.enabled")}</div>
            <div className={styles.workScheduleSwitchStatus}>
              {enabled ? t("work_schedule.status_active") : t("work_schedule.status_inactive")}
            </div>
          </div>
          <label className={styles.workScheduleSwitch} title={t("work_schedule.enabled_help")}>
            <input type="checkbox" checked={Boolean(enabled)} onChange={(e) => setEnabled(e.target.checked)} />
            <span className={styles.workScheduleSwitchTrack} />
          </label>
        </div>
      </div>

      {!enabled ? (
        <div className={styles.workScheduleNotice}>
          <span className={styles.workScheduleNoticeIcon}>ⓘ</span>
          <span>{t("work_schedule.disabled_notice")}</span>
        </div>
      ) : null}

      <div className={styles.workScheduleSection}>
        <div className={styles.workScheduleSectionHeader}>
          <div className={styles.workScheduleSectionTitle}>{t("work_schedule.mode_title")}</div>
          <div className={styles.workScheduleSectionHelp}>{t("work_schedule.mode_help")}</div>
        </div>
        <div className={styles.workScheduleSectionBody}>
          <div className={styles.workScheduleModeGrid}>
            <ModeCard
              active={mode === "recurring"}
              title={t("work_schedule.recurring")}
              badge={t("work_schedule.recurring_badge")}
              description={t("work_schedule.recurring_desc")}
              onClick={() => setMode("recurring")}
            />
            <ModeCard
              active={mode === "custom_dates"}
              title={t("work_schedule.custom")}
              badge={t("work_schedule.custom_badge")}
              description={t("work_schedule.custom_desc")}
              onClick={() => setMode("custom_dates")}
            />
          </div>
        </div>
      </div>

      {mode === "recurring" ? (
        <>
          <div className={styles.workScheduleSection}>
            <div className={styles.workScheduleSectionHeader}>
              <div className={styles.workScheduleSectionTitle}>{t("work_schedule.recurring_section")}</div>
              <div className={styles.workScheduleSectionHelp}>{t("work_schedule.recurring_section_help")}</div>
            </div>
            <div className={styles.workScheduleSectionBody}>
              <div className={styles.workScheduleStack}>
                <div className={styles.workScheduleGrid2}>
                  <div className={styles.workScheduleField}>
                    <label className={styles.workScheduleLabel}>{t("work_schedule.workday_start")}</label>
                    <input type="time" className={styles.workScheduleControl} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                  </div>
                  <div className={styles.workScheduleField}>
                    <label className={styles.workScheduleLabel}>{t("work_schedule.workday_end")}</label>
                    <input type="time" className={styles.workScheduleControl} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                  </div>
                </div>
                {invalidRange ? <div className={styles.workScheduleErrorText}>{t("work_schedule.invalid_range")}</div> : null}
              </div>
            </div>
          </div>

          <div className={styles.workScheduleSection}>
            <div className={styles.workScheduleSectionHeader}>
              <div className={styles.workScheduleSectionTitle}>{t("work_schedule.slot_section")}</div>
              <div className={styles.workScheduleSectionHelp}>{t("work_schedule.slot_section_help")}</div>
            </div>
            <div className={styles.workScheduleSectionBody}>
              <div className={styles.workScheduleStack}>
                <div className={styles.workScheduleChoiceGrid}>
                  <Choice
                    checked={slotGeneration === "duration"}
                    name="slot-generation"
                    value="duration"
                    title={t("work_schedule.by_duration")}
                    description={t("work_schedule.by_duration_desc")}
                    onChange={() => setSlotGeneration("duration")}
                  />
                  <Choice
                    checked={slotGeneration === "appointment_count"}
                    name="slot-generation"
                    value="appointment_count"
                    title={t("work_schedule.by_count")}
                    description={t("work_schedule.by_count_desc")}
                    onChange={() => setSlotGeneration("appointment_count")}
                  />
                </div>

                {slotGeneration === "appointment_count" ? (
                  <div className={styles.workScheduleField}>
                    <label className={styles.workScheduleLabel}>{t("work_schedule.appointments_count")}</label>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      className={styles.workScheduleControl}
                      value={appointmentCount}
                      onChange={(e) => setAppointmentCount(Number(e.target.value))}
                    />
                    <div className={styles.workScheduleHelp}>{t("work_schedule.appointments_count_help")}</div>
                  </div>
                ) : (
                  <SelectField
                    label={t("work_schedule.slot_step")}
                    value={Number(slotMinutes)}
                    onChange={(e) => setSlotMinutes(Number(e.target.value))}
                  >
                    {SLOT_OPTIONS.map((value) => (
                      <option key={value} value={value}>{t("work_schedule.minutes", { count: value })}</option>
                    ))}
                  </SelectField>
                )}

                <div className={styles.workSchedulePreviewBox}>
                  <div className={styles.workSchedulePreviewLabel}>{t("work_schedule.generated_preview")}</div>
                  {recurringPreview.length ? (
                    <div className={styles.workScheduleChips}>
                      {recurringPreview.map((value) => <span key={value} className={styles.workScheduleChip}>{value}</span>)}
                    </div>
                  ) : (
                    <div className={styles.workSchedulePreviewEmpty}>{t("work_schedule.no_preview")}</div>
                  )}
                </div>

                <div className={styles.workScheduleClientBox}>
                  <div className={styles.workScheduleClientIcon}>i</div>
                  <div>
                    <div className={styles.workScheduleClientTitle}>{t("work_schedule.what_clients_see")}</div>
                    <div className={styles.workScheduleClientText}>{t("work_schedule.recurring_client_help")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.workScheduleSection}>
            <div className={styles.workScheduleSectionHeader}>
              <div className={styles.workScheduleSectionTitle}>{t("work_schedule.custom_section")}</div>
              <div className={styles.workScheduleSectionHelp}>{t("work_schedule.custom_section_help")}</div>
            </div>
            <div className={styles.workScheduleSectionBody}>
              <div className={styles.workScheduleStack}>
                <div className={styles.workScheduleField}>
                  <label className={styles.workScheduleLabel}>{t("work_schedule.import_format")}</label>
                  <textarea
                    className={styles.workScheduleControl}
                    rows={7}
                    value={text}
                    onChange={(e) => { setText(e.target.value); setPreview(null); }}
                    placeholder={t("work_schedule.import_example")}
                  />
                  <div className={styles.workScheduleHelp}>{t("work_schedule.import_format_help")}</div>
                </div>

                <div className={styles.workScheduleGrid2}>
                  <SelectField
                    label={t("work_schedule.import_behavior")}
                    help={importMode === "add" ? t("work_schedule.import_add_help") : t("work_schedule.import_replace_help")}
                    value={importMode}
                    onChange={(e) => setImportMode(e.target.value)}
                  >
                    <option value="add">{t("work_schedule.import_add")}</option>
                    <option value="replace_future">{t("work_schedule.import_replace")}</option>
                  </SelectField>
                  <SelectField
                    label={t("work_schedule.collision_behavior")}
                    help={collisionMode === "add" ? t("work_schedule.collision_add_help") : t("work_schedule.collision_replace_help")}
                    value={collisionMode}
                    onChange={(e) => setCollisionMode(e.target.value)}
                  >
                    <option value="add">{t("work_schedule.collision_add")}</option>
                    <option value="replace_day">{t("work_schedule.collision_replace")}</option>
                  </SelectField>
                </div>

                <div className={styles.workScheduleActions}>
                  <button type="button" className={styles.workScheduleButton} onClick={doPreview} disabled={!text.trim() || Boolean(busy)}>
                    {busy === "preview" ? t("work_schedule.checking") : t("work_schedule.preview_btn")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.workScheduleButton} ${styles.workSchedulePrimary}`}
                    onClick={doImport}
                    disabled={!preview?.items?.length || Boolean(busy)}
                  >
                    {busy === "import" ? t("work_schedule.saving") : t("work_schedule.save_import_btn")}
                  </button>
                </div>

                {preview ? (
                  <div className={styles.workScheduleImportPreview}>
                    <div className={styles.workScheduleImportPreviewHead}>
                      <div>
                        <div className={styles.workScheduleImportPreviewTitle}>{t("work_schedule.preview_title")}</div>
                        <div className={styles.workScheduleImportPreviewMsg}>
                          {preview.items?.length ? t("work_schedule.preview_ready") : t("work_schedule.preview_empty")}
                        </div>
                      </div>
                      <span className={styles.workSchedulePreviewCount}>{previewCount}</span>
                    </div>
                    {preview.items?.length ? (
                      <div className={styles.workSchedulePreviewRows}>
                        {preview.items.map((item) => (
                          <div key={item.date} className={styles.workSchedulePreviewRow}>
                            <div className={styles.workSchedulePreviewDate}>{formatDate(item.date)}</div>
                            <div className={styles.workSchedulePreviewTimes}>
                              {(item.times || []).map((time) => <span className={styles.workScheduleChip} key={`${item.date}-${time}`}>{time}</span>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {preview.errors?.length ? (
                      <div className={styles.workScheduleErrorPanel}>
                        <div className={styles.workScheduleErrorTitle}>{t("work_schedule.import_errors")}</div>
                        {preview.errors.map((item, index) => (
                          <div className={styles.workScheduleErrorLine} key={`${item.line}-${index}`}>
                            {t("work_schedule.line_error", { line: item.line, text: item.text, error: item.error })}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className={styles.workScheduleClientBox}>
                  <div className={styles.workScheduleClientIcon}>i</div>
                  <div>
                    <div className={styles.workScheduleClientTitle}>{t("work_schedule.what_clients_see")}</div>
                    <div className={styles.workScheduleClientText}>{t("work_schedule.custom_client_help")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.workScheduleSection}>
            <div className={styles.workScheduleSectionHeader}>
              <div className={styles.workScheduleSectionTitle}>{t("work_schedule.manual_title")}</div>
              <div className={styles.workScheduleSectionHelp}>{t("work_schedule.manual_help")}</div>
            </div>
            <div className={styles.workScheduleSectionBody}>
              <div className={styles.workScheduleGrid3}>
                <div className={styles.workScheduleField}>
                  <label className={styles.workScheduleLabel}>{t("work_schedule.date")}</label>
                  <input type="date" className={styles.workScheduleControl} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                </div>
                <div className={styles.workScheduleField}>
                  <label className={styles.workScheduleLabel}>{t("work_schedule.time")}</label>
                  <input type="time" className={styles.workScheduleControl} value={newTime} onChange={(e) => setNewTime(e.target.value)} />
                </div>
                <div className={styles.workScheduleActions}>
                  <button type="button" className={`${styles.workScheduleButton} ${styles.workSchedulePrimary}`} onClick={addOne} disabled={!newDate || !newTime || Boolean(busy)}>
                    {t("work_schedule.add_slot")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.workScheduleSection}>
            <div className={styles.workScheduleSectionHeader}>
              <div className={styles.workScheduleScheduleTop}>
                <div>
                  <div className={styles.workScheduleSectionTitle}>{t("work_schedule.schedule_title")}</div>
                  <div className={styles.workScheduleSectionHelp}>{t("work_schedule.schedule_help")}</div>
                </div>
                <span className={`${styles.workScheduleScheduleStatus} ${!status?.has_future_dates ? styles.workScheduleScheduleStatusWarn : ""}`}>
                  {status?.configured_until
                    ? t("work_schedule.configured_until", { date: formatDate(status.configured_until) })
                    : t("work_schedule.no_future_dates")}
                </span>
              </div>
            </div>
            <div className={styles.workScheduleSectionBody}>
              {loading ? <div className={styles.workSchedulePreviewEmpty}>{t("work_schedule.loading")}</div> : null}
              {!loading && !status?.has_future_dates ? (
                <div className={styles.workScheduleNotice}>
                  <span className={styles.workScheduleNoticeIcon}>ⓘ</span>
                  <span>{t("work_schedule.no_future_help")}</span>
                </div>
              ) : null}

              {!loading && groupedRows.length ? (
                <div className={styles.workScheduleScheduleDays}>
                  {groupedRows.map(({ date, items }) => (
                    <div className={styles.workScheduleDayRow} key={date}>
                      <div className={styles.workScheduleDayDate}>{formatDate(date)}</div>
                      <div className={styles.workScheduleSlotList}>
                        {items.map((row) => (
                          editing?.id === row.id ? (
                            <div className={styles.workScheduleEditRow} key={row.id}>
                              <input
                                type="date"
                                className={styles.workScheduleControl}
                                value={editing.date}
                                onChange={(e) => setEditing((current) => ({ ...current, date: e.target.value }))}
                              />
                              <input
                                type="time"
                                className={styles.workScheduleControl}
                                value={editing.time}
                                onChange={(e) => setEditing((current) => ({ ...current, time: e.target.value }))}
                              />
                              <div className={styles.workScheduleActions}>
                                <button type="button" className={`${styles.workScheduleButton} ${styles.workSchedulePrimary}`} onClick={saveEdit} disabled={Boolean(busy)}>{t("work_schedule.save")}</button>
                                <button type="button" className={`${styles.workScheduleButton} ${styles.workScheduleGhost}`} onClick={() => setEditing(null)}>{t("work_schedule.cancel")}</button>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.workScheduleSlotItem} key={row.id}>
                              <span className={styles.workScheduleSlotTime}>{row.time}</span>
                              <button
                                type="button"
                                className={styles.workScheduleIconBtn}
                                title={t("work_schedule.edit")}
                                aria-label={t("work_schedule.edit")}
                                onClick={() => setEditing({ id: row.id, date: row.date, time: row.time })}
                              >
                                {t("work_schedule.edit")}
                              </button>
                              <button
                                type="button"
                                className={`${styles.workScheduleIconBtn} ${styles.workScheduleIconDanger}`}
                                title={t("work_schedule.delete")}
                                aria-label={t("work_schedule.delete")}
                                disabled={busy === `delete-${row.id}`}
                                onClick={() => deleteOne(row.id)}
                              >
                                {t("work_schedule.delete")}
                              </button>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}

      <div className={styles.workScheduleSection}>
        <div className={styles.workScheduleSectionHeader}>
          <div className={styles.workScheduleSectionTitle}>{t("work_schedule.planning_section")}</div>
          <div className={styles.workScheduleSectionHelp}>{t("work_schedule.planning_section_help")}</div>
        </div>
        <div className={styles.workScheduleSectionBody}>
          <SelectField
            label={t("work_schedule.minimum_ahead")}
            value={Number(minAheadMinutes)}
            onChange={(e) => setMinAheadMinutes(Number(e.target.value))}
          >
            {MIN_AHEAD_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value === 0 ? t("work_schedule.minimum_ahead_now") : t("work_schedule.minutes", { count: value })}
              </option>
            ))}
          </SelectField>
        </div>
      </div>

      {flash ? (
        <div className={`${styles.workScheduleFlash} ${flash.type === "ok" ? styles.workScheduleFlashOk : styles.workScheduleFlashError}`} style={{ marginTop: 14 }}>
          {flash.text}
        </div>
      ) : null}
    </div>
  );
}
